/**
 * Finder 3 etapes — meuble → besoin → critere → recommandation.
 * Moteur : window.LMDH_FINDER (assets/lmdh-finder.js).
 * Contenus éditables : data-finder-config (snippets/finder-config.liquid).
 * Les cartes meuble restent de vrais <a> crawlables ; le JS intercepte le clic.
 */
class ProductFinder extends HTMLElement {
  connectedCallback() {
    this.total = 3;
    this.answers = { meuble: null, besoin: null, critere: null, coussins: null };
    this.measureOpen = false;
    this.hasInteracted = false;
    this.config = this.readConfig();

    this.steps = new Map(
      [...this.querySelectorAll('[data-finder-step]')].map((el) => [Number(el.dataset.finderStep), el])
    );
    this.label = this.querySelector('[data-finder-label]');
    this.segments = [...this.querySelectorAll('[data-finder-seg]')];
    this.backButton = this.querySelector('[data-finder-back]');
    this.resultRoot = this.querySelector('[data-finder-result-root]');
    this.resultStep = this.steps.get(this.total + 1);

    this.addEventListener('click', this.onClick);
    this.sync({ scroll: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  readConfig() {
    const node = this.querySelector('[data-finder-config]');
    if (!node) return { labels: {}, meubles: {}, products: {} };
    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return { labels: {}, meubles: {}, products: {} };
    }
  }

  get engine() {
    return window.LMDH_FINDER || null;
  }

  get current() {
    return Number(this.dataset.step || 1);
  }

  /* lmdh 07/10/2026 (proprietaire) : pour un canape, la question des coussins (etape 5 du balisage) suit la question 1
     et s affiche comme sa suite (« Etape 1 sur 3 ») ; les autres meubles passent directement a l etape 2.
     Le resultat reste l etape total + 1. */
  estCanape(meuble) {
    return ['canape_droit', 'canape_angle', 'canape_u'].includes(meuble);
  }

  ordre() {
    return this.estCanape(this.answers.meuble) && this.steps.has(5) ? [1, 5, 2, 3] : [1, 2, 3];
  }

  estResultat(step) {
    return step === this.total + 1;
  }

  rang(step) {
    return step === 5 ? 1 : step;
  }

  suivante() {
    const o = this.ordre();
    const i = o.indexOf(this.current);
    return i >= 0 && i < o.length - 1 ? o[i + 1] : this.total + 1;
  }

  precedente() {
    const o = this.ordre();
    const i = o.indexOf(this.current);
    if (i > 0) return o[i - 1];
    if (this.estResultat(this.current)) return o[o.length - 1];
    return 1;
  }

  onClick = (event) => {
    const pick = event.target.closest('[data-finder-pick]');
    if (pick) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      const kind = pick.dataset.finderPick;
      const value = pick.dataset.value;
      if (!kind || !value) return;
      this.answers[kind] = value;
      if (kind === 'meuble' && !this.estCanape(value)) this.answers.coussins = null;
      this.go(this.suivante());
      return;
    }

    const chip = event.target.closest('[data-finder-chip]');
    if (chip) {
      event.preventDefault();
      this.go(Number(chip.dataset.finderChip));
      return;
    }

    if (event.target.closest('[data-finder-back]')) {
      event.preventDefault();
      this.go(this.precedente());
      return;
    }

    if (event.target.closest('[data-finder-restart]')) {
      event.preventDefault();
      this.reset();
      return;
    }

    if (event.target.closest('[data-finder-measure-toggle]')) {
      event.preventDefault();
      this.measureOpen = !this.measureOpen;
      this.renderResult();
    }
  };

  reset() {
    this.answers = { meuble: null, besoin: null, critere: null, coussins: null };
    this.measureOpen = false;
    this.clearResult();
    this.go(1);
  }

  clearResult() {
    if (this.resultRoot) this.resultRoot.innerHTML = '';
    if (this.resultStep) {
      this.resultStep.hidden = true;
      this.resultStep.setAttribute('hidden', '');
    }
  }

  go(step) {
    const next = [...this.ordre(), this.total + 1].includes(step) ? step : 1;
    this.dataset.step = String(next);
    this.sync({ scroll: false });
    this.hasInteracted = true;
    /* lmdh 07/10/2026 (critique de l accueil, P1) : le premier choix ne faisait jamais defiler (hasInteracted encore
       faux au premier appel) ; sur telephone, apres « Chaise » en bas de grille, la question suivante restait a -95 px,
       sous l en-tete. Chaque etape ramene le finder sous l en-tete si sa question n est pas a l ecran, et donne le
       focus a la question (lecteur d ecran, clavier). */
    const etape = this.estResultat(next) ? this.resultRoot : this.steps.get(next);
    const question = etape?.querySelector('.finder__question');
    const cible = question || etape || this;
    const header = document.querySelector('[data-header]');
    const haut = Math.max(0, header?.getBoundingClientRect().bottom || 0) + 8;
    const top = cible.getBoundingClientRect().top;
    if (top < haut || top > window.innerHeight * 0.6) window.LMH?.scrollToTarget?.(this);
    if (question) {
      question.setAttribute('tabindex', '-1');
      question.focus({ preventScroll: true });
    }
  }

  sync({ scroll = false } = {}) {
    const step = this.current;
    const isResult = this.estResultat(step);

    this.steps.forEach((element, index) => {
      const show = index === step;
      element.hidden = !show;
      if (show) element.removeAttribute('hidden');
      else element.setAttribute('hidden', '');
    });

    if (!isResult) this.clearResult();

    if (this.label) {
      this.label.textContent = isResult
        ? this.dataset.resultLabel || 'Deine Empfehlung'
        : (this.dataset.stepOf || 'Schritt __C__ von __T__')
            .replace('__C__', String(this.rang(step)))
            .replace('__T__', String(this.total));
    }

    this.segments.forEach((seg, i) => {
      const active = isResult || this.rang(step) >= i + 1;
      seg.classList.toggle('is-active', active);
    });

    if (this.backButton) {
      const showBack = step !== 1;
      this.backButton.hidden = !showBack;
      if (showBack) this.backButton.removeAttribute('hidden');
      else this.backButton.setAttribute('hidden', '');
    }

    this.updateRappels();
    this.updateSelectionState();

    if (isResult) this.renderResult();

    if (scroll) {
      window.LMH?.scrollToTarget?.(this);
    }
  }

  labelOf(kind, value) {
    const fromConfig = this.config.labels?.[kind]?.[value];
    if (fromConfig) return fromConfig;
    return this.engine?.labelOf?.(kind, value) || '';
  }

  meubleNoun(meuble) {
    const nouns = {
      canape_droit: 'dein Sofa',
      canape_angle: "dein Ecksofa",
      canape_u: 'dein Sofa in U-Form',
      convertible: 'dein Klick-Klack-Sofa',
      fauteuil: 'deinen Sessel',
      fauteuil_cabriolet: 'deinen Clubsessel',
      fauteuil_oreilles: 'deinen Ohrensessel',
      chaise: 'deinen Stuhl',
    };
    if (nouns[meuble]) return nouns[meuble];
    const label = this.labelOf('meuble', meuble);
    return label ? label.toLowerCase() : 'dein Möbelstück';
  }

  qtyMessage(meuble) {
    const override = this.config.meubles?.[meuble]?.qty;
    if (override) return override;
    return this.engine?.quantite?.(meuble) || null;
  }

  measureGuide(meuble) {
    const cfg = this.config.meubles?.[meuble]?.mesure;
    const fallback = this.engine?.mesure?.(meuble) || {};
    if (!cfg) return fallback;

    const hasContent =
      (cfg.label && cfg.label.trim()) ||
      (cfg.methode && cfg.methode.trim()) ||
      (cfg.note && cfg.note.trim()) ||
      (Array.isArray(cfg.lignes) && cfg.lignes.length);

    if (!hasContent) return fallback;

    return {
      label: cfg.label || fallback.label || '',
      methode: cfg.methode || fallback.methode || '',
      note: cfg.note || fallback.note || '',
      lignes: Array.isArray(cfg.lignes) && cfg.lignes.length ? cfg.lignes : fallback.lignes || [],
    };
  }

  productFromKey(key) {
    if (!key) return null;
    const eng = this.engine?.PRODUCTS?.[key];
    const base = eng
      ? { ...eng }
      : { id: key, name: '', price: '', href: '#', colors: '' };
    return this.enrichProduct(base);
  }

  /* lmdh 05/10/2026 (rapport 352, U25) : pour un canape d angle ou en U, un produit vendu en sets (EasyCover) s annonce
     a la piece : un set est prevu pour un canape droit. */
  prixPour(product) {
    /* lmdh 07/10/2026 : une housse de coussin s annonce a l unite */
    if (/^easydrap/.test(product?.id || '')) return { prix: product?.price || '', note: 'pro Bezug, einer pro Kissen' };
    const m = this.answers?.meuble;
    if (product?.pricePiece && (m === 'canape_angle' || m === 'canape_u')) return { prix: product.pricePiece, note: 'ein Teil pro Sitz, Rückenlehne und Armlehne' };
    /* lmdh 07/10/2026 (critique de l accueil, P1) : une housse a enfiler pour un angle ou un U s annonce au prix des
       2 ou 3 housses (« Des 34,90 EUR » pour un angle qui en demande deux) ; EasyCover, EasyDrap et jete gardent le leur. */
    /* lmdh 07/10/2026 (seconde critique de l accueil, P1) : un jete par partie, comme le dit la FAQ de l accueil (2 pour
       un angle, 3 pour un U) ; il s annoncait au prix d un seul. */
    if (product?.id === 'jete' && (m === 'canape_angle' || m === 'canape_u') && product?.priceMin > 0) {
      const nj = m === 'canape_u' ? 3 : 2;
      return { prix: 'Ab ' + this.euros(product.priceMin * nj), note: 'für ' + nj + ' Überwürfe' };
    }
    const n = this.nombreHousses(product);
    if (n > 1 && product?.priceMin > 0) return { prix: 'Ab ' + this.euros(product.priceMin * n), note: 'für ' + n + ' Bezüge' };
    return { prix: product?.price || '', note: '' };
  }

  /* lmdh 07/10/2026 (seconde critique de l accueil, P1) : pour un angle ou un U, le prix d une alternative disait
     « Des 99,80 EUR » sans dire que c etait celui de 2 housses. La note courte suit le prix ; la note des pieces
     EasyCover, longue, reste sur la carte principale. */
  noteAlt(product) {
    const note = this.prixPour(product).note;
    return note && note.length <= 30 ? ` <span class="finder__alt-note">· ${this.escape(note)}</span>` : '';
  }

  nombreHousses(product) {
    const m = this.answers?.meuble;
    const n = m === 'canape_angle' ? 2 : m === 'canape_u' ? 3 : 1;
    return n > 1 && product?.id && !this.engine?.pose?.(product.id) ? n : 1;
  }

  /* la fiche ouverte depuis le finder sait la forme : son assistant des tailles part de l angle ou du U */
  avecForme(href, product) {
    if (this.nombreHousses(product) < 2 || !href || href === '#') return href;
    try {
      const u = new URL(href, window.location.origin);
      u.searchParams.set('forme', this.answers.meuble === 'canape_u' ? 'u' : 'angle');
      return u.pathname + u.search + u.hash;
    } catch (e) {
      return href;
    }
  }

  /* lmdh 07/10/2026 (proprietaire) : pour un angle ou un U, la photo d angle de la fiche (vue 2 du premier gris pour
     l extensible, le jacquard et le velours ; coloris nomme pour l EasyDrap) ; EasyCover garde sa photo. */
  imageFor(product) {
    const m = this.answers?.meuble;
    if ((m === 'canape_angle' || m === 'canape_u') && product?.imageAngle && (this.nombreHousses(product) > 1 || product.angleExplicite)) return product.imageAngle;
    return product?.image || '';
  }

  euros(cents) {
    return window.LMH?.formatMoney ? window.LMH.formatMoney(cents) : (cents / 100).toFixed(2).replace('.', ',') + '\u00a0€';
  }

  enrichProduct(base) {
    if (!base) return null;
    const id = base.id;
    const override = (id && this.config.products?.[id]) || {};
    return {
      ...base,
      id,
      name: override.title || base.name,
      price: override.price || base.price,
      pricePiece: override.price_piece || '',
      priceMin: Number(override.price_min || 0),
      imageAngle: override.image_angle || '',
      angleExplicite: override.angle_explicite === true,
      href: override.url || base.href,
      image: override.image || base.image || '',
      colors: override.colors || base.colors,
      why: override.why || '',
      diff: override.diff || '',
      showReview: override.showReview !== false,
      rating: override.rating || '',
      ratingCount: Number(override.ratingCount || 0),
      review: override.review || null,
    };
  }

  buildWhy(product, meuble, critere, engineWhy, entreeDeGamme) {
    const template = product?.why;
    let why = template
      ? template.replace(/\{meuble\}/g, this.meubleNoun(meuble))
      : engineWhy || '';
    // lmdh : la phrase de prix n'est ajoutée que si le moteur confirme l'entrée de gamme de la famille de meuble
    if (critere === 'budget' && why && entreeDeGamme && !/moins chère|prix le plus bas|günstigste|niedrigste[rn]? Preis/i.test(why)) {
      why += ' Das ist der günstigste Bezug unseres Sortiments, um dieses Möbelstück ganz zu beziehen.';
    }
    return why;
  }

  buildReview(product, engineReview) {
    if (product?.showReview === false) return null;
    const name = product?.review?.name || engineReview?.name || '';
    const text = product?.review?.text || engineReview?.text || '';
    if (!name && !text) return null;
    return {
      name,
      text,
      initial: (name || '?').charAt(0).toUpperCase(),
    };
  }

  updateRappels() {
    // lmdh 30/09/2026 (rapport 334, A3) : pour un angle ou un U, le nombre de housses depend du modele
    // (deux housses a enfiler, ou des pieces EasyCover) : on ne l annonce qu avec la recommandation.
    const qty = ['canape_angle', 'canape_u'].includes(this.answers.meuble) ? null : this.qtyMessage(this.answers.meuble);
    this.querySelectorAll('[data-finder-qty-hint]').forEach((node) => {
      if (qty && this.current === 2) {
        node.hidden = false;
        node.removeAttribute('hidden');
        node.textContent = qty;
      } else {
        node.hidden = true;
        node.setAttribute('hidden', '');
        node.textContent = '';
      }
    });

    const rappel = this.querySelector('[data-finder-rappel-deux]');
    if (rappel) {
      const a = this.labelOf('meuble', this.answers.meuble);
      const b = this.labelOf('besoin', this.answers.besoin);
      if (a && b && this.current === 3) {
        rappel.textContent = `${a} · ${b}`;
        rappel.hidden = false;
        rappel.removeAttribute('hidden');
      } else {
        rappel.textContent = '';
        rappel.hidden = true;
        rappel.setAttribute('hidden', '');
      }
    }
  }

  updateSelectionState() {
    this.querySelectorAll('[data-finder-pick]').forEach((el) => {
      const kind = el.dataset.finderPick;
      const on = this.answers[kind] === el.dataset.value;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  resolveRecommendation() {
    const F = this.engine;
    const { meuble, besoin, critere } = this.answers;
    if (!F?.recommend || !meuble || !besoin || !critere) return null;

    const reco = F.recommend(meuble, besoin, critere, { coussins: this.answers.coussins });
    const meubleCfg = this.config.meubles?.[meuble];
    const forcedKey = meubleCfg?.productKey;

    let main = forcedKey ? this.productFromKey(forcedKey) : this.enrichProduct(reco.main);
    if (!main?.name && reco.main) main = this.enrichProduct(reco.main);
    if (!main) return null;

    const alts = (reco.alts || [])
      .map((a) => {
        const product = this.enrichProduct(a.product);
        if (!product || product.id === main.id) return null;
        return {
          product,
          diff: product.diff || a.diff || '',
        };
      })
      .filter(Boolean);

    // lmdh 30/09/2026 (rapport 334, A3/E2) : EasyCover, EasyDrap et jete ne se mesurent pas d accoudoir a
    // accoudoir : la mesure et la quantite suivent la housse conseillee.
    const pose = F.pose?.(main.id);
    return {
      main,
      why: this.buildWhy(main, meuble, critere, reco.why, reco.entreeDeGamme === true),
      review: this.buildReview(main, reco.review),
      alts,
      qty: pose ? pose.qty || null : this.qtyMessage(meuble),
      mesure: pose ? { ...pose.mesure } : this.measureGuide(meuble),
    };
  }

  renderResult() {
    const root = this.resultRoot;
    if (!root) return;

    const resolved = this.resolveRecommendation();
    if (!resolved) {
      this.clearResult();
      return;
    }

    const { meuble, besoin, critere } = this.answers;
    const { main, why, review, alts, qty, mesure } = resolved;
    const strings = this.dataset;

    const chips = [
      { step: 1, label: this.labelOf('meuble', meuble) },
      ...(this.estCanape(meuble) && this.answers.coussins ? [{ step: 5, label: this.labelOf('coussins', this.answers.coussins) }] : []),
      { step: 2, label: this.labelOf('besoin', besoin) },
      { step: 3, label: this.labelOf('critere', critere) },
    ];

    const altsTitle =
      alts.length === 1
        ? strings.altsOne || 'Eine weitere Option'
        : strings.altsMany || 'Zwei weitere Optionen';

    const measureLabel = this.measureOpen
      ? strings.measureHide || 'Maßanleitung ausblenden'
      : mesure.label || strings.measureShow || 'Wie messe ich?';

    const isPage = this.dataset.layout === 'page';

    const recapHtml = `
      <div class="finder__recap">
        <span>${this.escape(strings.recapPrefix || 'Deine Auswahl:')}</span>
        ${chips
          .map(
            (c) => `
          <button type="button" class="finder__chip" data-finder-chip="${c.step}">
            <span>${this.escape(c.label)}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
          </button>`
          )
          .join('')}
      </div>
      <h3 class="finder__question">${this.escape(strings.resultTitle || 'Diesen Bezug empfehlen wir dir')}</h3>`;

    const resultCardHtml = `
      <article class="card finder__result">
        <div class="finder__result-media">
          ${
            this.imageFor(main)
              ? `<img src="${this.escape(this.imageFor(main))}" alt="${this.escape(main.name)}" width="800" height="600" loading="lazy">`
              : `<div class="finder__result-placeholder" aria-hidden="true"></div>`
          }
        </div>
        <div class="finder__result-body">
          <h4 class="finder__result-title">${this.escape(main.name)}</h4>
          <div class="finder__result-meta">
            <span class="finder__result-price">${this.escape(this.prixPour(main).prix)}</span>${this.prixPour(main).note ? `<span class="text-sm text-secondary">${this.escape(this.prixPour(main).note)}</span>` : ''}
            ${main.colors ? `<span class="text-sm text-secondary">${this.escape(main.colors)}</span>` : ''}
          </div>
          <p class="finder__result-why">${this.escape(why || '')}</p>
          ${
            review
              ? `<div class="finder__review">
            <span class="finder__review-avatar" aria-hidden="true">${this.escape(review.initial || '')}</span>
            <div class="finder__review-copy">
              <div class="finder__review-head">
                <span class="finder__review-name">${this.escape(review.name || '')}</span>
                <span class="pill pill--trust">${this.escape(strings.verified || 'Verifizierter Kauf')}</span>
              </div>
              ${
                main.rating
                  ? `<span class="text-sm text-secondary">★ ${this.escape(main.rating)}${
                      main.ratingCount ? ` · ${this.escape(main.ratingCount.toLocaleString('de-DE'))} ${this.escape(strings.reviewsWord || 'Bewertungen')}` : ''
                    }</span>`
                  : ''
              }
              <p class="finder__review-text">„${this.escape(review.text || '')}“</p>
            </div>
          </div>`
              : ''
          }
        </div>
      </article>`;

    const asideHtml = `
      ${
        qty
          ? `<div class="finder__qty">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
              <span>${this.escape(qty)}</span>
            </div>`
          : ''
      }
      <a class="btn btn--primary finder__cta" href="${this.escape(this.avecForme(main.href, main))}">${this.escape(
        strings.resultCta || 'Diesen Bezug ansehen'
      )}</a>
      <button type="button" class="finder__measure-toggle" data-finder-measure-toggle>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21.3 8.7 8.7 21.3a1 1 0 0 1-1.4 0l-4.6-4.6a1 1 0 0 1 0-1.4L15.3 2.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4Z"/><path d="m7.5 10.5 2 2"/><path d="m10.5 7.5 2 2"/><path d="m13.5 4.5 2 2"/><path d="m4.5 13.5 2 2"/></svg>
        <span>${this.escape(measureLabel)}</span>
      </button>
      ${
        this.measureOpen
          ? `<div class="card finder__measure">
              <p class="finder__measure-method">${this.escape(mesure.methode || '')}</p>
              ${
                (mesure.lignes || []).length
                  ? `<div class="finder__measure-table" role="list">
                      ${(mesure.lignes || [])
                        .map(
                          (row) => `
                        <div class="finder__measure-row" role="listitem">
                          <span>${this.escape(row.places)}</span>
                          <span class="text-secondary">${this.escape(row.cm)}</span>
                        </div>`
                        )
                        .join('')}
                    </div>`
                  : ''
              }
              <p class="text-sm text-secondary">${this.escape(mesure.note || '')}</p>
            </div>`
          : ''
      }`;

    const altsHtml = alts.length
      ? `<p class="finder__alts-title">${this.escape(altsTitle)}</p>
             <div class="finder__alts">
               ${alts
                 .map(
                   (a) => `
                 <a class="card finder__alt" href="${this.escape(this.avecForme(a.product.href, a.product))}">
                   <span class="finder__alt-media">
                     ${
                       this.imageFor(a.product)
                         ? `<img src="${this.escape(this.imageFor(a.product))}" alt="" width="128" height="128" loading="lazy">`
                         : ''
                     }
                   </span>
                   <span class="finder__alt-copy">
                     <span class="finder__alt-name">${this.escape(a.product.name)}</span>
                     <span class="finder__alt-price">${this.escape(this.prixPour(a.product).prix)}${this.noteAlt(a.product)}</span>
                     <span class="text-sm text-secondary">${this.escape(a.diff)}</span>
                   </span>
                   <svg class="finder__alt-arrow" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                 </a>`
                 )
                 .join('')}
             </div>`
      : '';

    if (isPage) {
      root.innerHTML = `
        <div class="finder__outcome finder__outcome--split">
          ${recapHtml}
          <div class="finder__outcome-main">${resultCardHtml}</div>
          <div class="finder__aside">${asideHtml}</div>
        </div>
        ${altsHtml}
        <button type="button" class="finder__restart" data-finder-restart>
          ${this.escape(strings.restart || 'Neu beginnen')}
        </button>
      `;
    } else {
      root.innerHTML = `
        ${recapHtml}
        ${resultCardHtml}
        ${asideHtml}
        ${altsHtml}
        <button type="button" class="finder__restart" data-finder-restart>
          ${this.escape(strings.restart || 'Neu beginnen')}
        </button>
      `;
    }
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('product-finder')) {
  customElements.define('product-finder', ProductFinder);
}
